import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import {
  LayoutDashboard, ScanBarcode, Package, FileText, FileSpreadsheet, Truck,
  Users, Building2, ShoppingCart, Calculator, BarChart3, Settings, LogOut,
  Wrench, Menu, X, FileMinus2, HelpCircle, Receipt, BookOpen, History,
  ChevronsLeft, ChevronsRight, ClipboardList, ShieldCheck, Wallet, UserCog, Inbox,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import HelpButton from "@/components/HelpButton";
import ThemeToggle from "@/components/ThemeToggle";
import useDashboardPrefs from "@/hooks/useDashboardPrefs";
import useOrderNotifications, { loadOrderNotifPrefs } from "@/hooks/useOrderNotifications";
import bus from "@/lib/eventBus";
import { useBrand } from "@/context/BrandContext";

const NAV = [
  { section: "Diario" },
  { to: "/", label: "Dashboard", icon: LayoutDashboard, exact: true, policy: "dashboard.view", module: "dashboard" },
  { to: "/pos", label: "POS / Venta", icon: ScanBarcode, policy: "pos.create_cart", module: "pos" },
  { to: "/sales-orders", label: "Cola de Órdenes", icon: ClipboardList, policy: "orders.view_queue", module: "orders_queue" },
  { to: "/cash-sessions", label: "Caja Z / X", icon: Wallet, policy: "pos.create_cart", module: "cash_sessions" },

  { section: "Ventas" },
  { to: "/sales", label: "Facturas", icon: FileText, policy: "sales.view", module: "invoices" },
  { to: "/quotations", label: "Cotizaciones", icon: FileSpreadsheet, policy: "sales.view", module: "quotations" },
  { to: "/delivery-notes", label: "Conduces", icon: Truck, policy: "sales.view", module: "delivery_notes" },
  { to: "/credit-debit-notes", label: "Notas NC / ND", icon: FileMinus2, policy: "sales.view", module: "credit_notes" },

  { section: "Compras" },
  { to: "/purchases", label: "Compras", icon: ShoppingCart, policy: "purchases.view", module: "purchases" },
  { to: "/expenses", label: "Gastos", icon: Wallet, policy: "purchases.view", module: "purchases" },
  { to: "/ecf-recibidos", label: "e-CF Recibidos", icon: Inbox, policy: "purchases.view", module: "purchases" },
  { to: "/suppliers", label: "Suplidores", icon: Building2, policy: "contacts.view", module: "suppliers" },

  { section: "Finanzas" },
  { to: "/fiscal", label: "Control fiscal", icon: ShieldCheck, policy: "reports.view" },
  { to: "/customer-statements", label: "Estados de cuenta", icon: BookOpen, policy: "finance.view", module: "cxc" },
  { to: "/receipts", label: "Recibos I / E", icon: Receipt, policy: "finance.view", module: "receipts" },
  { to: "/accounting", label: "Contabilidad", icon: Calculator, policy: "accounting.view", module: "accounting" },
  { to: "/cxc-migration", label: "CxC Migración", icon: History, policy: "accounting.view", module: "cxc" },

  { section: "RRHH" },
  { to: "/payroll", label: "Nómina", icon: UserCog, policy: "accounting.view", module: "hr_payroll" },

  { section: "Catálogo" },
  { to: "/inventory", label: "Inventario", icon: Package, policy: "inventory.view", module: "inventory" },
  { to: "/customers", label: "Clientes", icon: Users, policy: "contacts.view", module: "clients" },

  { section: "Análisis" },
  { to: "/reports", label: "Reportes", icon: BarChart3, policy: "reports.view", module: "reports" },
  { to: "/reports/orders-audit", label: "Auditoría Órdenes", icon: ShieldCheck, policy: "reports.audit_orders", module: "audit" },

  { section: "Sistema" },
  { to: "/help", label: "Ayuda", icon: HelpCircle, module: "ayuda" },
  { to: "/settings", label: "Ajustes", icon: Settings, adminOnly: true, module: "settings" },
  { to: "/settings/roles", label: "Roles y Políticas", icon: ShieldCheck, adminOnly: true, module: "users" },
];

export default function Layout() {
  const { user, logout, can, policies } = useAuth();
  const { brand, isEnabled } = useBrand();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { prefs, loaded, patch } = useDashboardPrefs();

  // Iter 48 — sidebar colapsable
  const [collapsed, setCollapsed] = useState(false);
  const [autoExpand, setAutoExpand] = useState(false);
  const [hoverExpand, setHoverExpand] = useState(false); // temporal por hover
  const hoverTimer = useRef(null);

  // Iter 50 — Badge de conteo de órdenes pendientes en el sidebar
  const [pendingOrdersCount, setPendingOrdersCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/sales-orders/count-pending");
        if (!cancelled) setPendingOrdersCount(data.count || 0);
      } catch (_) { /* silencio */ }
    };
    fetchCount();
    const t = setInterval(fetchCount, 15000);
    // Iter 58.1 — refresco inmediato ante cambios (facturar/regresar/cancelar)
    const offBus = bus.on("orders:refresh", fetchCount);
    return () => { cancelled = true; clearInterval(t); offBus(); };
  }, []);

  // Iter 51 — Notificaciones (sonido + desktop) cuando entra una orden nueva
  const [notifPrefs] = useState(() => loadOrderNotifPrefs());
  useOrderNotifications(pendingOrdersCount, {
    enabled: notifPrefs.enabled && can && can("orders.view_queue"),
    sound: notifPrefs.sound,
    desktop: notifPrefs.desktop,
  });

  useEffect(() => {
    if (loaded) {
      setCollapsed(!!prefs.sidebar_collapsed);
      setAutoExpand(!!prefs.sidebar_auto_expand);
    }
  }, [loaded, prefs.sidebar_collapsed, prefs.sidebar_auto_expand]);

  const showFull = !collapsed || hoverExpand;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const toggleCollapse = () => {
    const next = !collapsed;
    setCollapsed(next);
    patch({ sidebar_collapsed: next }).catch(() => {});
  };
  const toggleAutoExpand = () => {
    const next = !autoExpand;
    setAutoExpand(next);
    patch({ sidebar_auto_expand: next }).catch(() => {});
  };

  const onMouseEnter = () => {
    if (collapsed && autoExpand) {
      clearTimeout(hoverTimer.current);
      hoverTimer.current = setTimeout(() => setHoverExpand(true), 120);
    }
  };
  const onMouseLeave = () => {
    clearTimeout(hoverTimer.current);
    hoverTimer.current = setTimeout(() => setHoverExpand(false), 200);
  };

  return (
    <div className="h-screen flex bg-background overflow-hidden">
      {/* Sidebar */}
      <aside
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`fixed lg:static z-40 inset-y-0 left-0 bg-card border-r border-border flex flex-col h-screen lg:h-full transition-[width,transform] duration-200 ${
          mobileOpen ? "translate-x-0 w-64" : "-translate-x-full lg:translate-x-0"
        } ${showFull ? "lg:w-64" : "lg:w-16"}`}
        data-testid="app-sidebar"
        data-collapsed={collapsed ? "true" : "false"}
      >
        <div className={`px-4 py-4 border-b border-border flex items-center gap-2 ${showFull ? "" : "lg:justify-center lg:px-2"}`}>
          <div className="w-8 h-8 bg-primary rounded-md grid place-items-center text-primary-foreground shrink-0">
            <Wrench className="w-5 h-5" strokeWidth={2.5} />
          </div>
          {showFull && (
            <div className="min-w-0 flex-1">
              <div className="text-[10px] tracking-[0.18em] uppercase text-muted-foreground font-bold truncate" title={brand.product_name}>
                {brand.company_name || brand.display_tagline || "Suite"}
              </div>
              <div className="font-bold text-sm leading-tight truncate" title={brand.product_name}>
                {brand.company_name ? brand.product_name : brand.display_short}
              </div>
            </div>
          )}
          <button
            className="lg:hidden ml-auto"
            onClick={() => setMobileOpen(false)}
            data-testid="close-sidebar-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Iter 48 — controles del sidebar (colapsar + auto-expand) */}
        <div className={`hidden lg:flex items-center border-b border-border px-2 py-1.5 gap-1 ${showFull ? "justify-between" : "justify-center"}`}>
          <button
            onClick={toggleCollapse}
            className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            title={collapsed ? "Expandir menú" : "Colapsar menú"}
            data-testid="sidebar-collapse-toggle"
          >
            {collapsed ? <ChevronsRight className="w-4 h-4" /> : <ChevronsLeft className="w-4 h-4" />}
          </button>
          {showFull && (
            <label
              className="flex items-center gap-1.5 text-[10px] text-muted-foreground cursor-pointer"
              title="Al pasar el mouse, expande automáticamente"
            >
              <span>Auto-expandir</span>
              <input
                type="checkbox"
                checked={autoExpand}
                onChange={toggleAutoExpand}
                className="accent-primary w-3 h-3"
                data-testid="sidebar-auto-expand-toggle"
              />
            </label>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3 space-y-0.5">
          {(() => {
            // Primero filtra items individuales por módulo/policy/role
            const visible = NAV.filter((item) => {
              if (item.section) return true;
              if (item.adminOnly && user?.role !== "admin") return false;
              if (item.policy && policies && policies.length > 0 && !can(item.policy)) return false;
              if (item.module && !isEnabled(item.module)) return false;
              return true;
            });
            // Luego elimina secciones que quedaron sin items debajo
            const result = [];
            for (let i = 0; i < visible.length; i++) {
              const it = visible[i];
              if (it.section) {
                // Si el siguiente item también es section (o no hay más) → sección vacía
                const next = visible[i + 1];
                if (!next || next.section) continue;
              }
              result.push(it);
            }
            return result;
          })().map((item, idx) =>
            item.section ? (
              showFull ? (
                <div key={idx} className="overline px-3 mt-4 mb-1">{item.section}</div>
              ) : (
                <div key={idx} className="border-t border-border/40 mx-2 my-2" />
              )
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setMobileOpen(false)}
                data-testid={`nav-${item.to === "/" ? "home" : item.to.replace(/\//g, "-").replace(/^-/, "")}`}
                title={showFull ? undefined : item.label}
                className={({ isActive }) =>
                  `flex items-center relative ${showFull ? "gap-3 px-3" : "justify-center px-0"} py-2 text-sm rounded-md transition-colors ${
                    isActive
                      ? "bg-primary/10 text-primary font-semibold" + (showFull ? " border-l-2 border-primary pl-[10px]" : "")
                      : "text-foreground/80 hover:bg-accent hover:text-foreground"
                  }`
                }
              >
                <item.icon className="w-4 h-4 shrink-0" strokeWidth={2} />
                {showFull && <span className="truncate">{item.label}</span>}
                {/* Iter 50 — Badge para "Cola de Órdenes" */}
                {item.to === "/sales-orders" && pendingOrdersCount > 0 && (
                  <span
                    className={`${showFull ? "ml-auto" : "absolute top-1 right-1"} bg-amber-500 text-white text-[10px] font-black rounded-full min-w-[18px] h-[18px] px-1 flex items-center justify-center animate-pulse`}
                    data-testid="nav-orders-badge"
                    title={`${pendingOrdersCount} orden(es) pendiente(s)`}
                  >
                    {pendingOrdersCount > 99 ? "99+" : pendingOrdersCount}
                  </span>
                )}
              </NavLink>
            )
          )}
        </nav>

        <div className="border-t border-border p-2">
          {showFull ? (
            <>
              <div className="px-2 py-1 mb-2">
                <div className="text-[10px] text-muted-foreground">Sesión</div>
                <div className="text-sm font-semibold truncate">{user?.email}</div>
                <div className="overline mt-0.5">{user?.role}</div>
              </div>
              <div className="mb-2">
                <ThemeToggle />
              </div>
              <Button
                variant="outline"
                className="w-full justify-start text-sm"
                onClick={handleLogout}
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
              </Button>
            </>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <ThemeToggle compact />
              <button
                onClick={handleLogout}
                title="Cerrar sesión"
                className="p-1.5 rounded hover:bg-accent text-muted-foreground hover:text-destructive"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-foreground/40 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 h-screen lg:h-full overflow-hidden">
        <header className="lg:hidden sticky top-0 z-20 bg-card border-b border-border px-4 py-3 flex items-center gap-3 shrink-0">
          <button onClick={() => setMobileOpen(true)} data-testid="open-sidebar-btn">
            <Menu className="w-5 h-5" />
          </button>
          <div className="font-bold">{brand.company_name || brand.display_short}</div>
          <div className="ml-auto">
            <ThemeToggle compact />
          </div>
        </header>
        <main className="flex-1 min-w-0 overflow-y-auto" data-testid="app-main-scroll">
          <Outlet />
        </main>
      </div>

      <HelpButton />
    </div>
  );
}
